import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: { sitekey: string; callback: (token: string) => void }
      ) => void;
      remove: (el: HTMLElement) => void;
      reset: (el: HTMLElement) => void;
    };
  }
}

interface CloudflareTurnstileProps {
  onVerify: (token: string) => void;
  onReset?: () => void;
  shouldReset?: boolean;
}

const CloudflareTurnstile = ({
  onVerify,
  onReset,
  shouldReset,
}: CloudflareTurnstileProps) => {
  const siteKey =
    window.location.origin === 'https://agentportal.hurtigruten.com'
      ? '0x4AAAAAABlVXEBqiWJNhmMA' // PROD key
      : '0x4AAAAAABlAtc9skMJiQQDz'; // STAGING key
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetNodeRef = useRef<HTMLDivElement | null>(null);

  // Reset effect
  useEffect(() => {
    if (shouldReset && widgetNodeRef.current && window.turnstile) {
      window.turnstile.reset(widgetNodeRef.current);
      onReset?.();
    }
  }, [shouldReset, onReset]);

  useEffect(() => {
    function renderWidget() {
      if (widgetRef.current && window.turnstile) {
        window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          callback: onVerify,
        });
        widgetNodeRef.current = widgetRef.current;
      }
    }

    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        renderWidget();
      };
    } else {
      renderWidget();
    }

    return () => {
      if (widgetNodeRef.current && window.turnstile) {
        window.turnstile.remove(widgetNodeRef.current);
      }
    };
  }, [siteKey, onVerify]);

  return <div ref={widgetRef} />;
};

export default CloudflareTurnstile;
